import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import adminApi from "@/lib/adminApi";
import { Loader2 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line,
} from "recharts";

const DATE_PRESETS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "all", label: "All time" },
];

function computeBounds(range) {
  if (range === "all") return {};
  const days = parseInt(range, 10);
  if (!Number.isFinite(days)) return {};
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - days);
  return { from: from.toISOString(), to: to.toISOString() };
}

const CardShell = ({ title, subtitle, children, testid, empty }) => (
  <div className="bg-white border border-[var(--border)] rounded-[4px] p-5" data-testid={testid}>
    <div className="flex items-baseline justify-between mb-4">
      <h2 className="text-xs uppercase tracking-[0.18em] text-[var(--pharma-blue)] font-semibold">{title}</h2>
      {subtitle && <span className="text-xs text-[var(--slate)]">{subtitle}</span>}
    </div>
    {empty ? (
      <div className="text-sm text-[var(--slate)] py-6 text-center">No data in the selected range.</div>
    ) : (
      children
    )}
  </div>
);

const CHART_COLOR = "#1560BD";           // pharma-blue
const CHART_COLOR_MUTED = "#0A2540";     // navy
const AXIS_COLOR = "#64748B";            // slate
const GRID_COLOR = "#F8FAFC";            // bg-alt

const chartTheme = {
  fontSize: 11,
  fontFamily: "Manrope, sans-serif",
  color: AXIS_COLOR,
};

const barTooltipStyle = {
  contentStyle: {
    background: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: 4,
    fontSize: 12,
    color: "#1F2937",
  },
  cursor: { fill: "rgba(21, 96, 189, 0.06)" },
};

const AdminInsights = () => {
  const [range, setRange] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await adminApi.get("/insights", { params: computeBounds(range) });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load insights");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout title="Insights">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[var(--navy)] tracking-tight" data-testid="insights-title">
            Insights
          </h1>
          <p className="mt-2 text-sm text-[var(--slate)] max-w-xl">
            Lead distribution across type, source of interest, country, and time.
          </p>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-[var(--slate)] mb-1">Date range</label>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="text-sm px-3 py-2 border border-[var(--border)] rounded-[3px] bg-white focus:outline-none focus:border-[var(--pharma-blue)]"
            data-testid="insights-range"
          >
            {DATE_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-[3px] px-3 py-2" data-testid="insights-error">
          {String(error)}
        </div>
      )}

      {loading ? (
        <div className="p-12 text-center text-sm text-[var(--slate)] flex items-center justify-center gap-2" data-testid="insights-loading">
          <Loader2 size={16} className="animate-spin" /> Loading insights…
        </div>
      ) : !data ? null : (
        <>
          <div className="mb-4 bg-white border border-[var(--border)] rounded-[4px] p-5" data-testid="insights-total-card">
            <div className="text-xs uppercase tracking-wider text-[var(--slate)]">Total leads in range</div>
            <div className="mt-1 font-heading text-4xl font-semibold text-[var(--navy)]" data-testid="insights-total">
              {data.total}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CardShell title="Leads over time" subtitle="Daily" testid="insights-over-time" empty={data.over_time.length === 0}>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.over_time} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                    <CartesianGrid stroke={GRID_COLOR} vertical={false} />
                    <XAxis dataKey="date" tick={chartTheme} stroke={AXIS_COLOR} />
                    <YAxis allowDecimals={false} tick={chartTheme} stroke={AXIS_COLOR} />
                    <Tooltip {...barTooltipStyle} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke={CHART_COLOR}
                      strokeWidth={2}
                      dot={{ fill: CHART_COLOR, r: 3 }}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardShell>

            <CardShell title="Leads by type" testid="insights-by-type" empty={data.by_type.length === 0}>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.by_type} layout="vertical" margin={{ top: 4, right: 16, left: 24, bottom: 0 }}>
                    <CartesianGrid stroke={GRID_COLOR} horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={chartTheme} stroke={AXIS_COLOR} />
                    <YAxis type="category" dataKey="label" width={130} tick={chartTheme} stroke={AXIS_COLOR} />
                    <Tooltip {...barTooltipStyle} />
                    <Bar dataKey="count" fill={CHART_COLOR} radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardShell>

            <CardShell title="Leads by source of interest" subtitle="Top 15" testid="insights-by-source" empty={data.by_source.length === 0}>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.by_source} layout="vertical" margin={{ top: 4, right: 16, left: 24, bottom: 0 }}>
                    <CartesianGrid stroke={GRID_COLOR} horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={chartTheme} stroke={AXIS_COLOR} />
                    <YAxis type="category" dataKey="label" width={180} tick={chartTheme} stroke={AXIS_COLOR} />
                    <Tooltip {...barTooltipStyle} />
                    <Bar dataKey="count" fill={CHART_COLOR_MUTED} radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardShell>

            <CardShell title="Leads by country" subtitle="Top 10" testid="insights-by-country" empty={data.by_country.length === 0}>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.by_country} layout="vertical" margin={{ top: 4, right: 16, left: 24, bottom: 0 }}>
                    <CartesianGrid stroke={GRID_COLOR} horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={chartTheme} stroke={AXIS_COLOR} />
                    <YAxis type="category" dataKey="label" width={140} tick={chartTheme} stroke={AXIS_COLOR} />
                    <Tooltip {...barTooltipStyle} />
                    <Bar dataKey="count" fill={CHART_COLOR} radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardShell>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminInsights;
